import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'

const AuthScreen = () => {
  const [isSignUp, setIsSignUp] = useState(true)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const { signUp, signIn, connectionStatus } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirect = searchParams.get('redirect')

  // Show connection status
  useEffect(() => {
    if (connectionStatus === 'failed') {
      setError('Connection failed. Please refresh the page and try again.')
    }
  }, [connectionStatus])

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    // Connection check
    if (connectionStatus !== 'connected') {
      setError('Connection error. Please refresh the page and try again.')
      setLoading(false)
      return
    }

    // Enhanced validation
    if (!email?.trim() || !password?.trim()) {
      setError('Please fill in all fields.')
      setLoading(false)
      return
    }

    if (!validateEmail(email.trim())) {
      setError('Please enter a valid email address.')
      setLoading(false)
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.')
      setLoading(false)
      return
    }

    if (isSignUp && !name?.trim()) {
      setError('Please enter your name.')
      setLoading(false)
      return
    }

    try {
      if (isSignUp) {
        console.log('🚀 Starting signup process...')
        setSuccess('Creating your account...')
        
        const { data, error } = await signUp(email.trim(), password, {
          full_name: name.trim(),
          tracking_mode: 'cycle'
        })

        if (error) {
          throw error
        }

        console.log('✅ Signup successful, redirecting...')
        setSuccess('Account created successfully! Redirecting...')
        
        // Short delay to show success message
        setTimeout(() => {
          if (redirect === 'payment') {
            navigate('/unlock')
          } else {
            navigate('/setup')
          }
        }, 1500)
        
      } else {
        console.log('🚀 Starting signin process...')
        const { data, error } = await signIn(email.trim(), password)

        if (error) {
          throw error
        }

        console.log('✅ Signin successful, redirecting...')
        
        // Redirect based on redirect param
        if (redirect === 'payment') {
          navigate('/unlock')
        } else {
          navigate('/')
        }
      }
    } catch (error) {
      console.error('Auth error:', error)
      let errorMessage = 'Authentication failed. Please try again.'
      
      if (error.message) {
        errorMessage = error.message
        
        // Auto-switch to sign in if user already exists
        if (error.message.includes('An account with this email already exists')) {
          setIsSignUp(false)
          setSuccess('Switching to sign in...')
          setTimeout(() => setSuccess(''), 2000)
        }
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-white px-6 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-md mx-auto"
      >
        {/* Connection Status */}
        {connectionStatus === 'checking' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-sm text-blue-600">Connecting to server...</p>
            </div>
          </div>
        )}

        {connectionStatus === 'failed' && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-600">Connection failed. Please refresh the page and try again.</p>
          </div>
        )}

        {connectionStatus === 'connected' && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-green-600">✅ Connected successfully</p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <button
            onClick={() => navigate(redirect === 'payment' ? '/unlock' : '/')}
            className="text-sm text-gray-500 mb-6 hover:text-black transition-colors"
          >
            ← Back
          </button>
          
          <h1 className="italiana-font text-3xl text-black mb-4 leading-tight">
            {isSignUp ? 'Create Your Account 🌙' : 'Welcome Back 💫'}
          </h1>
          
          <p className="text-sm text-gray-600 leading-relaxed">
            {isSignUp 
              ? 'Set up your secure account to save your cycle data and sync across devices'
              : 'Sign in to access your personalized cycle guidance'
            }
          </p>
          
          {redirect === 'payment' && (
            <div className="bg-gold bg-opacity-10 p-4 rounded-lg mt-4">
              <p className="text-sm text-gray-700">
                🔐 Creating your account first ensures your premium access is securely linked
              </p>
            </div>
          )}
        </div>

        {/* Benefits for Sign Up */}
        {isSignUp && (
          <div className="bg-gold bg-opacity-10 p-6 rounded-lg mb-8">
            <h3 className="font-semibold text-black mb-3 text-center">
              ✨ Your Account Includes:
            </h3>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-start space-x-2">
                <span className="gold-accent">•</span>
                <span>Secure cloud storage for your cycle data</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="gold-accent">•</span>
                <span>Access from any device, anywhere</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="gold-accent">•</span>
                <span>Personalized daily guidance</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="gold-accent">•</span>
                <span>Premium features when you upgrade</span>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {isSignUp && (
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Your Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
                placeholder="Enter your first name"
                required
                maxLength={50}
                disabled={loading || connectionStatus !== 'connected'}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
              placeholder="your@email.com"
              required
              autoComplete="email"
              disabled={loading || connectionStatus !== 'connected'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-black mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold focus:border-gold transition-all"
              placeholder="Create a secure password"
              minLength={6}
              required
              autoComplete={isSignUp ? "new-password" : "current-password"}
              disabled={loading || connectionStatus !== 'connected'}
            />
            {isSignUp && (
              <p className="text-xs text-gray-500 mt-1">
                Minimum 6 characters
              </p>
            )}
          </div>

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <motion.button
            type="submit"
            disabled={loading || connectionStatus !== 'connected'}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>{isSignUp ? 'Creating Account...' : 'Signing In...'}</span>
              </div>
            ) : (
              isSignUp ? 'Create My Account →' : 'Sign In →'
            )}
          </motion.button>
        </form>

        {/* Toggle */}
        <div className="text-center mt-8">
          <button
            type="button"
            onClick={() => {
              setIsSignUp(!isSignUp)
              setError('')
              setSuccess('')
              setEmail('')
              setPassword('')
              setName('')
            }}
            className="text-sm text-gray-500 hover:text-black transition-colors"
            disabled={loading}
          >
            {isSignUp 
              ? 'Already have an account? Sign in'
              : "Don't have an account? Sign up"
            }
          </button>
        </div>

        {/* Skip Option - only show if not coming from payment */}
        {redirect !== 'payment' && (
          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
              disabled={loading}
            >
              Skip for now (continue without account)
            </button>
          </div>
        )}

        {/* Privacy Note */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500">
            🔒 Your data is encrypted and secure. We never share your personal information.
          </p>
        </div>

        {/* Debug Info (only in development) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="mt-4 p-3 bg-gray-100 rounded text-xs text-gray-600">
            <strong>Debug:</strong> Connection: {connectionStatus}
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default AuthScreen